/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import '../../styles/app.css';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';
import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import { useI18n } from '@oneaudi/i18n-context';
import TopLevelNav from '../navigation/TopLevelNav';
import SecondLevelNavDesktop from '../navigation/SecondLevelNavDesktop';
import useToggle from '../../hooks/use-toggle';
import useActiveLink from '../../hooks/use-active-link';
import { useTracking } from '../../hooks/use-tracking';
import { TrackingServiceContext, useInPageNavContext } from '../../context';
import { messages } from '../../i18n/messages';
import { useMount } from '../../hooks/use-mount';
import useSticky from '../../hooks/use-sticky';
import {
  topLevelRelatedElements,
  trackImpressionEvent,
  trackToggleEvent,
} from '../../utils/tracking-utils';
import { useSubLinks } from '../../hooks/use-sub-links';

interface IProps {
  trackingService?: TrackingServiceV2;
}

const Container = styled.div<{ $removeStickiness: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  background-color: ${(props) => props.theme.colors.background};
  opacity: 1;
  transition: ${(props) => props.theme.easing.transition};
  transition-property: opacity;
  will-change: opacity;
  padding: 0;
  width: 100%;
  position: ${(props) => (props.$removeStickiness ? '' : 'sticky')};
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;

  &.sticky {
    z-index: 100;
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoints.lg}px) {
    width: auto;
    padding: 0 var(--page-margin);
  }
  @media screen and (min-width: ${(props) => props.theme.breakpoints.xxl}px) {
    padding: 0 calc((100% - 1728px) / 2);
  }
  div[data-aue-type='container'] {
    width: 100%;
  }
`;

const InpageNav: React.FC<IProps> = () => {
  const [visible, toggle] = useToggle(false);
  const {
    topLevelActiveLink,
    sameLevelActiveLink,
    secondLevelActiveLink,
    subNavActive,
    sameLevelInpageNavigation,
    priorityNavActive,
    priorityNavigation,
    removeStickiness,
    impressionTracking,
  } = useInPageNavContext();
  const { trackingService } = useContext(TrackingServiceContext);
  const tracking = useTracking(trackingService);
  const { containerRef, isSticky } = useSticky();
  const noContentMessage = useI18n(messages.no_navigation);
  const [isSublinksActiveUrl, setIsSublinksActiveUrl] = useState(false);
  const { subLinks } = useSubLinks(
    visible,
    sameLevelInpageNavigation,
    topLevelActiveLink,
    sameLevelActiveLink,
    secondLevelActiveLink,
    subNavActive,
  );

  useMount(() => {
    tracking?.appReady();
  });

  useEffect(() => {
    if (impressionTracking) {
      trackImpressionEvent(tracking, impressionTracking, sameLevelInpageNavigation, subLinks);
    }
  }, [impressionTracking]);

  const isSublinksActive = subNavActive?.subLinks?.active;

  useEffect(() => {
    const canToggle =
      topLevelActiveLink?.index === subNavActive?.index && subNavActive?.subLinks?.hasLinks;

    if (canToggle) {
      toggle();
      // keep subLinks nav open when switching between subnav menus
      if (isSublinksActive) toggle(true);
    }
  }, [sameLevelActiveLink, topLevelActiveLink]);

  // tracking for overflow/priority menu
  useEffect(() => {
    if (priorityNavActive && priorityNavActive?.isDesktop && priorityNavActive?.interaction > 1) {
      const relatedElements = priorityNavActive?.active
        ? topLevelRelatedElements(priorityNavigation)
        : [];
      trackToggleEvent(
        tracking,
        `inpage navigation - ${priorityNavActive?.active ? 'open' : 'close'} overflow items`,
        ['Overflow menu'],
        impressionTracking?.value,
        '',
        '',
        relatedElements,
      );
    }
  }, [priorityNavActive]);

  useEffect(() => {
    if (isSublinksActiveUrl) {
      toggle(true);
    }
  }, [isSublinksActiveUrl]);

  // used to render the active links based on url
  useActiveLink(setIsSublinksActiveUrl);

  return (
    <Container
      data-testid="inpage-navigation"
      id="inpagenav"
      ref={containerRef}
      className={`${isSticky ? 'sticky' : ''}`}
      $removeStickiness={removeStickiness ?? false}
    >
      {sameLevelInpageNavigation && sameLevelInpageNavigation?.length > 0 ? (
        <>
          <TopLevelNav visible={visible} />
          {/* large screens sub level navigation */}
          <SecondLevelNavDesktop visible={visible} toggle={toggle} />
        </>
      ) : (
        <Text tag="p">{noContentMessage}</Text>
      )}
    </Container>
  );
};

export default InpageNav;
